import React, { useState } from 'react';
import { 
  AlertTriangle, Shield, FileText, Users, Clock, DollarSign,
  ChevronRight, Zap, X, CheckCircle
} from 'lucide-react';

const ChallengesSection = () => {
  const [activeChallenge, setActiveChallenge] = useState(0);

  const challenges = [
    {
      icon: Shield,
      title: 'NDIS Quality & Safeguards Audits',
      problem: 'Weeks of preparation stress before every audit',
      impact: 'Staff overtime, missed deadlines, compliance risks',
      solution: 'Automated audit-ready documentation at your fingertips',
      color: 'red'
    },
    {
      icon: FileText,
      title: 'Complex Documentation',
      problem: 'Multiple systems, duplicated data entry',
      impact: 'Hours wasted on paperwork instead of participant care',
      solution: 'Single source of truth for all participant records',
      color: 'orange'
    },
    {
      icon: AlertTriangle,
      title: 'Incident Tracking Gaps',
      problem: 'Missing or delayed incident reports',
      impact: 'Risk of non-compliance with NDIS Commission',
      solution: 'Real-time incident capture with automated escalation',
      color: 'yellow'
    },
    {
      icon: Users,
      title: 'Staff Compliance Monitoring',
      problem: 'Expired credentials going unnoticed',
      impact: 'Service delivery risks and audit failures',
      solution: 'Automatic alerts before credentials expire',
      color: 'purple'
    },
    {
      icon: Clock,
      title: 'Report Generation',
      problem: 'Days spent compiling NDIS reports manually',
      impact: 'Delayed submissions and data inconsistencies',
      solution: 'One-click NDIS-compliant report generation',
      color: 'blue'
    },
    {
      icon: DollarSign,
      title: 'Software Cost Escalation',
      problem: 'Per-user fees growing with your team',
      impact: 'Budget constraints limiting growth',
      solution: 'One-time build with predictable costs',
      color: 'green'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-red-500/20 backdrop-blur-sm rounded-full border border-red-500/30 mb-6 animate-bounce">
            <AlertTriangle className="text-red-400 mr-2" size={18} />
            <span className="text-sm font-medium text-red-300">The Pain Points We Solve</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            The Challenge <span className="bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">Australian NDIS Providers</span> Face
          </h2>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Running an NDIS organisation means juggling complex compliance requirements—we understand the struggle
          </p>
        </div>

        {/* Interactive Challenge Grid */}
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Challenge Cards */}
          <div className="space-y-4">
            {challenges.map((challenge, index) => {
              const Icon = challenge.icon;
              const isActive = activeChallenge === index;
              
              return (
                <div
                  key={index}
                  onClick={() => setActiveChallenge(index)}
                  className={`relative p-5 rounded-2xl cursor-pointer transition-all duration-500 transform ${
                    isActive 
                      ? 'bg-gradient-to-r from-red-500/20 to-orange-500/20 border-2 border-red-500/50 scale-[1.02] shadow-2xl shadow-red-500/20' 
                      : 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                      isActive ? 'bg-red-500 scale-110' : 'bg-white/10'
                    }`}>
                      <Icon className={isActive ? 'text-white' : 'text-slate-400'} size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-semibold text-lg mb-1 transition-colors ${isActive ? 'text-white' : 'text-slate-300'}`}>
                        {challenge.title}
                      </h3>
                      <p className={`text-sm transition-colors ${isActive ? 'text-slate-300' : 'text-slate-500'}`}>
                        {challenge.problem}
                      </p>
                    </div>
                    <ChevronRight className={`text-slate-500 transition-all duration-300 ${isActive ? 'rotate-90 text-red-400' : ''}`} size={20} />
                  </div>
                  
                  {/* Active indicator line */}
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-3/4 bg-gradient-to-b from-red-500 to-orange-500 rounded-full"></div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Challenge Detail Panel */}
          <div className="sticky top-24">
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-3xl border border-white/10 p-8 shadow-2xl">
              {/* Problem Section */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <X className="text-red-500" size={24} />
                  <span className="text-red-400 font-semibold uppercase text-sm tracking-wider">The Problem</span>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6">
                  <p className="text-white text-lg font-medium mb-2">{challenges[activeChallenge].problem}</p>
                  <p className="text-slate-400">{challenges[activeChallenge].impact}</p>
                </div>
              </div>

              {/* Arrow Animation */}
              <div className="flex justify-center my-6">
                <div className="flex flex-col items-center">
                  <div className="w-px h-8 bg-gradient-to-b from-red-500 to-care-teal"></div>
                  <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-care-teal"></div>
                </div>
              </div>

              {/* Solution Section */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="text-care-teal" size={24} />
                  <span className="text-care-teal font-semibold uppercase text-sm tracking-wider">GetCare Solution</span>
                </div>
                <div className="bg-care-teal/10 border border-care-teal/20 rounded-xl p-6">
                  <p className="text-white text-lg font-medium">{challenges[activeChallenge].solution}</p>
                </div>
              </div>

              {/* Progress Indicator */}
              <div className="mt-8 flex gap-2">
                {challenges.map((_, index) => (
                  <div
                    key={index}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      index === activeChallenge 
                        ? 'flex-[2] bg-gradient-to-r from-red-500 to-orange-500' 
                        : 'flex-1 bg-white/20'
                    }`}
                  ></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChallengesSection;
